import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try { await resetPassword(token, password); setDone(true); setTimeout(() => navigate("/login"), 1800); }
    catch (ex) { setErr(formatApiError(ex.response?.data?.detail) || ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <img src={LOGO_URL} alt="OnchainLyfe" className="h-8 mb-8" />
        {done ? (
          <div data-testid="reset-success">
            <h1 className="font-display text-3xl font-bold">Password updated</h1>
            <p className="text-zinc-400 text-sm mt-3">You can now log in with your new password. Redirecting…</p>
            <p className="text-sm text-zinc-500 mt-8">
              <Link to="/login" className="text-[#ccff00] hover:underline">Go to log in</Link>
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <h1 className="font-display text-3xl font-bold">Choose a new password</h1>
            <p className="text-zinc-400 text-sm mt-1 mb-6">Enter and confirm your new password.</p>
            {err && <div data-testid="reset-error" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">{err}</div>}
            <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">New password</label>
            <input data-testid="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full mb-1.5 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="••••••••" />
            <p className="text-[11px] text-zinc-500 mb-4">At least 8 characters with an uppercase, a lowercase and a number.</p>
            <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Confirm password</label>
            <input data-testid="reset-password-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full mb-6 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="••••••••" />
            <button data-testid="reset-submit" disabled={busy} className="w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-60">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
